/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { ensureUserSession } from '@/api/settings';

/** Ensures a backend user_info row exists once per account on login. */
export function useEnsureUserSession(): void {
  const { account } = useAuth();
  const gmin = account?.gmin ?? '';
  const seededFor = useRef<string | null>(null);

  useEffect(() => {
    // Signed out: drop the guard so the same account signing back in re-seeds.
    if (!gmin) {
      seededFor.current = null;
      return;
    }
    if (seededFor.current === gmin) return;
    seededFor.current = gmin;
    const [firstName, ...rest] = (account?.name ?? '').trim().split(/\s+/);
    void ensureUserSession(gmin, {
      firstName: firstName || undefined,
      lastName: rest.join(' ') || undefined,
      email: account?.username ?? undefined,
    }).catch(() => {
      // Best-effort: clear the guard so a later effect run retries (the endpoint is idempotent).
      seededFor.current = null;
    });
  }, [gmin, account]);
}
